import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

interface UndoItem {
  habitId: string;
  habitName: string;
  previousValue: number | null; // null means no previous completion
}

interface UndoContextValue {
  undoItem: UndoItem | null;
  triggerUndo: (item: UndoItem) => void;
  executeUndo: () => void;
  dismissUndo: () => void;
}

const UndoContext = createContext<UndoContextValue | null>(null);

const UNDO_TIMEOUT_MS = 4000;

interface UndoProviderProps {
  children: React.ReactNode;
  onUndo: (habitId: string) => Promise<void>;
}

export function UndoProvider({ children, onUndo }: UndoProviderProps) {
  const [undoItem, setUndoItem] = useState<UndoItem | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const dismissUndo = useCallback(() => {
    clearTimer();
    setUndoItem(null);
  }, [clearTimer]);

  const triggerUndo = useCallback(
    (item: UndoItem) => {
      clearTimer();
      setUndoItem(item);
      timerRef.current = setTimeout(() => {
        setUndoItem(null);
      }, UNDO_TIMEOUT_MS);
    },
    [clearTimer],
  );

  const executeUndo = useCallback(() => {
    if (undoItem) {
      clearTimer();
      const { habitId } = undoItem;
      setUndoItem(null);
      void onUndo(habitId);
    }
  }, [undoItem, clearTimer, onUndo]);

  const value = useMemo(
    () => ({ undoItem, triggerUndo, executeUndo, dismissUndo }),
    [undoItem, triggerUndo, executeUndo, dismissUndo],
  );

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

export function useUndo(): UndoContextValue {
  const ctx = useContext(UndoContext);
  if (!ctx) {
    throw new Error("useUndo must be used within an UndoProvider");
  }
  return ctx;
}
